import { startOfDay, startOfISOWeek, startOfMonth, startOfYear } from "date-fns/esm";

export type DateRangeType = "day" | "week" | "month" | "year";

export type DurationSeriesItem = {
	start: string | number | Date;
	duration: number;
};

/** Stable bucket key matching seriesTypeDateCheck grouping. */
export function seriesTypeDateStart(seriesType: DateRangeType, dateLeft: string | number | Date): Date | undefined {
	return (
		seriesType === "day"
			? startOfDay
			: seriesType === "week"
				? startOfISOWeek
				: seriesType === "month"
					? startOfMonth
					: seriesType === "year"
						? startOfYear
						: null
	)?.(new Date(dateLeft));
}

export function seriesBucketKey(seriesType: DateRangeType, date: string | number | Date): number {
	const start = seriesTypeDateStart(seriesType, date);
	return start?.getTime() ?? new Date(date).getTime();
}

/**
 * One pass over times into duration buckets, then project onto seriesRange categories.
 * Avoids O(categories x times) repeated filters.
 */
export function aggregateDurationsBySeriesBuckets(
	times: DurationSeriesItem[],
	seriesType: DateRangeType,
	seriesRange: Array<[Date, ...unknown[]]>,
	predicate?: (item: DurationSeriesItem) => boolean
): number[] {
	const buckets = new Map<number, number>();
	for (const item of times) {
		const duration = ~~item.duration;
		if (duration <= 0) continue;
		if (predicate && !predicate(item)) continue;
		const key = seriesBucketKey(seriesType, item.start);
		buckets.set(key, (buckets.get(key) ?? 0) + duration);
	}
	return seriesRange.map(([date]) => buckets.get(seriesBucketKey(seriesType, date)) ?? 0);
}

/** Index items by start-of-day ms for interval lookups. */
export function indexByStartOfDay<T extends { start: string | number | Date }>(
	items: T[],
	startOfDayFn: (d: Date) => Date
): Map<number, T[]> {
	const byDay = new Map<number, T[]>();
	for (const item of items) {
		const key = startOfDayFn(new Date(item.start)).getTime();
		const list = byDay.get(key);
		if (list) list.push(item);
		else byDay.set(key, [item]);
	}
	return byDay;
}

/** Collect items whose start day falls in [rangeStart, rangeEnd). */
export function collectItemsInDayRange<T>(
	byDay: Map<number, T[]>,
	rangeStart: Date,
	rangeEnd: Date,
	startOfDayFn: (d: Date) => Date,
	addDaysFn: (d: Date, n: number) => Date,
	isBeforeFn: (a: Date, b: Date) => boolean
): T[] {
	const collected: T[] = [];
	for (let d = startOfDayFn(rangeStart); isBeforeFn(d, rangeEnd); d = addDaysFn(d, 1)) {
		const dayItems = byDay.get(d.getTime());
		if (dayItems?.length) collected.push(...dayItems);
	}
	return collected;
}
